import dayjs from 'dayjs';
import { z } from 'zod';
import IBAN from 'iban';

// Enums
export enum ContractTerminationStatus {
  PENDING_TENANT_APPROVAL = 'pending_tenant_approval',
  READY_FOR_REVIEW = 'ready_for_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum ContractTerminationReason {
  APARTMENT_SIZE = 'apartment_size',
  AREA_POOR_SERVICES = 'area_poor_services',
  BUYING_PROPERTY = 'buying_property',
  DISTURBING_NEIGHBOURS_OR_BUILDING = 'disturbing_neighbours_or_building',
  FAMILY_SIZE_CHANGE = 'family_size_change',
  HOUSING_COSTS = 'housing_costs',
  MOVE_DUE_WORK = 'move_due_work',
  MOVE_TO_ANOTHER_APARTMENT_OF_SAME_OWNER = 'move_to_another_apartment_of_same_owner',
  MOVE_TO_ANOTHER_MUNICIPALITY = 'move_to_another_municipality',
  MOVE_TO_CARE_HOME_OR_HOSPITAL = 'move_to_care_home_or_hospital',
  MOVE_TO_RIGHT_OF_OCCUPANCY = 'move_to_right_of_occupancy',
  POOR_APARTMENT_CONDITION = 'poor_apartment_condition',
  POOR_APARTMENT_EQUIPMENT_OR_PROPERTIES = 'poor_apartment_equipment_or_properties',
  POOR_PROPERTY_CONDITION = 'poor_property_condition',
  POOR_PROPERTY_MAINTENANCE = 'poor_property_maintenance',
  POOR_TRANSPORTATION_OPTIONS = 'poor_transportation_options',
}

// Address
const newAddressSchema = z
  .object({
    recipientName: z.string().min(1).max(100),
    streetAddress: z.string().min(1).max(100),
    postalCode: z.string().length(5),
    city: z.string().min(1).max(100),
  })
  .strip();

// Bic code can be null for finnish bank accounts
const bicSchema = z.union([z.string().regex(/^[A-Z0-9]{8}$|^[A-Z0-9]{11}$/), z.null()]);

// Beneficiaries
const baseBeneficiarySchema = z
  .object({
    beneficiaryName: z.string().min(1),
    iban: z.string().toUpperCase().refine(IBAN.isValid),
    bic: bicSchema,
    phoneNumber: z.string().min(1),
    email: z.string().email(),
  })
  .strip();

export const existingBeneficiarySchema = baseBeneficiarySchema
  .extend({
    returnPercentage: z.string().nullable(),
  })
  .strip();

export const newBeneficiarySchema = baseBeneficiarySchema
  .extend({
    existingBeneficiaryId: z.number().nullable(),
    returnPercentage: z.number().max(100).min(1),
    removeExistingBeneficiary: z.boolean(),
    contractToTenantAssociationId: z.number().nullable(),
    phoneNumber: z.string().min(1),
    email: z.string().email(),
  })
  .strip();

// Base Termination Schema
const terminationBaseSchema = z
  .object({
    movingOutDate: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? null : val),
      z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
        .nullable()
    ),
    givingContactInfoAllowed: z.boolean(),
    masterKeyAccessAllowed: z.boolean(),
    hasPetsInApartment: z.boolean(),
    terminationReason: z.nativeEnum(ContractTerminationReason),
    endDate: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/),
    displaysSelf: z.boolean(),
    displayStartDate: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
        .optional()
    ),
    displayEndDate: z.preprocess(
      (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
      z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/)
        .optional()
    ),
    additionalDetails: z.string().max(1000),
    isCollateral: z.boolean().nullable(),
    collateralLender: z.string().nullable(),
    beneficiaries: z.array(baseBeneficiarySchema),
    newAddress: newAddressSchema,
  })
  .strip();

// Termination Notice (GET)
export const terminationNoticeSchema = terminationBaseSchema
  .extend({
    id: z.number(),
    status: z.nativeEnum(ContractTerminationStatus),
    files: z.array(z.string()),
    beneficiaries: z.array(existingBeneficiarySchema),
  })
  .strip();

// New Termination Notice (POST)
export const newTerminationNoticeSchema = terminationBaseSchema
  .extend({
    fileUuids: z.array(z.string().uuid()),
    beneficiaries: z.array(newBeneficiarySchema),
  })
  .strip()
  .refine(
    (data) => {
      if (!data.movingOutDate) return true; // Allow nullable
      const movingOutDate = dayjs(data.movingOutDate);
      const endDate = dayjs(data.endDate);
      const today = dayjs();
      return movingOutDate.isAfter(today) && !movingOutDate.isAfter(endDate);
    },
    { path: ['movingOutDate'] }
  )
  .refine(
    (data) => {
      if (!data.displayEndDate) return true; // Allow nullable
      const contractEndDate = dayjs(data.endDate);
      const displayStartDate = dayjs(data.displayStartDate);
      const displayEndDate = dayjs(data.displayEndDate);
      return displayEndDate.isSameOrAfter(displayStartDate) && contractEndDate.isSameOrAfter(displayEndDate);
    },
    { path: ['displayEndDate'] }
  );

export type NewAddress = z.infer<typeof newAddressSchema>;

export type BaseBeneficiary = z.infer<typeof baseBeneficiarySchema>;
export type ExistingBeneficiary = z.infer<typeof existingBeneficiarySchema>;
export type NewBeneficiary = z.infer<typeof newBeneficiarySchema>;

export type TerminationBase = z.infer<typeof terminationBaseSchema>;
export type TerminationNotice = z.infer<typeof terminationNoticeSchema>;
export type NewTerminationNotice = z.infer<typeof newTerminationNoticeSchema>;

export const terminationInstructionsSchema = z.object({
  instructionsRead: z.boolean().refine((val) => val === true),
});

export type TerminationInstructionsFormState = z.infer<typeof terminationInstructionsSchema>;
